import { useEffect, useMemo } from 'react';
import {
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  LocationContext,
  useSelectedLocationContext,
  useSetSelectedLocationContext,
  useUserOrganizationAccessLoadable,
} from './Model/Data';
import ShellRootLayout from './Shell/ShellRootLayout';
import { ProgressBackdrop } from './Shell/ProgressBackdrop';
import { useScopedTrace } from './Hooks/useScopedTrace';
import { useLocalStorage } from './Hooks/useLocalStorage';
import { usePostHogIdentify } from './Utilities/Instrumentation/usePostHogIdentify';
import { usePostHogGroups } from './Utilities/Instrumentation/usePostHogGroups';
import {
  firstAccessibleLocation,
  hasLocationAccess,
  LAST_VISITED_LOCATION,
  preferredAccessibleLocation,
} from './Access/accessRouteHelpers';
import { NoOrganizationAccessScreen } from './Access/NoOrganizationAccessScreen';
import { RootRoute } from './Access/RootRoute';
import {
  CasesToClientsRedirect,
  LocationScopedRoutes,
  RouteError,
} from './LocationScopedRoutes';
import { UserProfile } from './UserProfile/UserProfile';
import { RedeemPersonInvite } from './UserProfile/RedeemPersonInvite';

function RouteMigrator() {
  const trace = useScopedTrace('RouteMigrator');
  const location = useLocation();
  const navigate = useNavigate();

  const userOrganizationAccess = useUserOrganizationAccessLoadable();
  trace(
    `userOrganizationAccess contents: ${JSON.stringify(
      userOrganizationAccess?.organizations?.map((org) => ({
        organizationId: org.organizationId,
        locations: org.locations?.map((loc) => ({
          locationId: loc.locationId,
          personId: loc.personId,
          roles: loc.roles,
        })),
      }))
    )}`
  );

  const [lastVisitedLocation] = useLocalStorage<LocationContext | null>(
    LAST_VISITED_LOCATION,
    null
  );
  trace(`lastVisitedLocation contents: ${JSON.stringify(lastVisitedLocation)}`);

  const migrationTargetContext = useMemo(
    () =>
      userOrganizationAccess
        ? preferredAccessibleLocation(
            userOrganizationAccess,
            lastVisitedLocation
          )
        : null,
    [userOrganizationAccess, lastVisitedLocation]
  );

  useEffect(() => {
    if (userOrganizationAccess == null) {
      return;
    }

    if (migrationTargetContext == null) {
      trace('Could not migrate route.');
      return;
    }

    //TODO: Only do this if the old path is a valid/migrate-able path to begin with.
    const target =
      `/org/${migrationTargetContext.organizationId}/${migrationTargetContext.locationId}${location.pathname}` +
      `${location.search}${location.hash}`;
    trace(`Attempting to migrate route to: ${target}`);
    navigate(target);
  }, [
    trace,
    navigate,
    location,
    userOrganizationAccess,
    migrationTargetContext,
  ]);

  if (userOrganizationAccess != null && migrationTargetContext == null) {
    return <NoOrganizationAccessScreen />;
  }

  return (
    <ProgressBackdrop opaque>
      <p>Attempting to migrate a suspected old link:</p>
      <pre>{window.location.href}</pre>
      <p>
        If you are not redirected in a few seconds, please submit a bug report.
      </p>
    </ProgressBackdrop>
  );
}

type AuthorizedLocationContextWrapperProps = {
  organizationId: string;
  locationId: string;
};

function AuthorizedLocationContextWrapper({
  organizationId,
  locationId,
}: AuthorizedLocationContextWrapperProps) {
  const trace = useScopedTrace('LocationContext');

  const selectedLocationContext = useSelectedLocationContext();
  const setSelectedLocationContext = useSetSelectedLocationContext();
  const [, setLastVisitedLocation] = useLocalStorage<LocationContext | null>(
    LAST_VISITED_LOCATION,
    null
  );

  // We only need to change this on first load or when the location context actually changes.
  useEffect(() => {
    trace(`organizationId: '${organizationId}' -- locationId: '${locationId}'`);

    if (organizationId && locationId) {
      setSelectedLocationContext({ organizationId, locationId });
      setLastVisitedLocation({ organizationId, locationId });
    } else {
      trace(
        `Location context was NOT updated. ` +
          `organizationId: '${organizationId}'` +
          `locationId: '${locationId}'`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, locationId, trace, setSelectedLocationContext]);

  usePostHogGroups();

  usePostHogIdentify();

  return (
    // We need to wait for this to have a value before rendering the child tree; otherwise,
    // the tree will suspend as soon as a data dependency on selectedLocationContextState is encountered, and
    // the effect above (that actually sets the selectedLocationContextState) will not fire.
    selectedLocationContext !== null &&
      // As an added benefit, we can also use this component to show the spinner when switching locations.
      selectedLocationContext.organizationId === organizationId &&
      selectedLocationContext.locationId === locationId ? (
      <ShellRootLayout>
        {/* Location pages belong in the typed route tree with a switch policy. */}
        <LocationScopedRoutes />
      </ShellRootLayout>
    ) : (
      <ProgressBackdrop opaque>
        <p>Setting location...</p>
      </ProgressBackdrop>
    )
  );
}

function LocationContextWrapper() {
  const { organizationId, locationId } = useParams<{
    organizationId: string;
    locationId: string;
  }>();
  const userOrganizationAccess = useUserOrganizationAccessLoadable();

  const requestedLocationContext = useMemo(
    () =>
      organizationId && locationId
        ? { organizationId: organizationId, locationId: locationId }
        : null,
    [organizationId, locationId]
  );

  const fallbackLocationContext = useMemo(
    () =>
      userOrganizationAccess
        ? firstAccessibleLocation(userOrganizationAccess)
        : null,
    [userOrganizationAccess]
  );

  if (userOrganizationAccess == null) {
    return (
      <ProgressBackdrop opaque>
        <p>Loading access...</p>
      </ProgressBackdrop>
    );
  }

  if (
    requestedLocationContext == null ||
    !hasLocationAccess(userOrganizationAccess, requestedLocationContext)
  ) {
    if (fallbackLocationContext == null) {
      return <NoOrganizationAccessScreen />;
    }

    return (
      <Navigate
        to={`/org/${fallbackLocationContext.organizationId}/${fallbackLocationContext.locationId}/`}
        replace
      />
    );
  }

  return (
    <AuthorizedLocationContextWrapper
      organizationId={requestedLocationContext.organizationId}
      locationId={requestedLocationContext.locationId}
    />
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/org/:organizationId/:locationId/*"
        element={<LocationContextWrapper />}
      />
      <Route path="/me/redeemPersonInvite" element={<RedeemPersonInvite />} />
      <Route
        path="/me/*"
        element={
          <ShellRootLayout>
            <UserProfile />
          </ShellRootLayout>
        }
      />
      {/* The following routes are only kept for migration/fallback purposes. */}
      <Route path="/families/:familyId" element={<RouteMigrator />} />
      <Route path="/cases/*" element={<CasesToClientsRedirect />} />
      <Route path="/clients/*" element={<RouteMigrator />} />
      <Route path="/volunteers/*" element={<RouteMigrator />} />
      <Route path="/organizations/*" element={<RouteMigrator />} />
      <Route path="/communities/*" element={<RouteMigrator />} />
      <Route path="/settings/*" element={<RouteMigrator />} />
      <Route path="/support/*" element={<RouteMigrator />} />
      <Route path="/" element={<RootRoute />} />
      <Route path="*" element={<RouteError />} />
    </Routes>
  );
}
